import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { listInventoryMaps } from "../api/rest";
import { useTelemetryStore } from "../stores/telemetryStore";
import { useSiteId } from "./useSiteId";
import { useWarehouseId } from "./useWarehouseId";
import { activityMatches, liveCountStats, type LiveCountStats } from "../scene/liveCount";

/**
 * ADR 363 — the live count for the active warehouse, or null when no sweep is
 * streaming into it. One hook feeds the /live KPI card and the /inventory banner
 * so the two can never disagree. Re-evaluates every 5 s so pace and ETA move
 * even between deltas.
 */
export function useLiveCount(): LiveCountStats | null {
  const siteId = useSiteId();
  const warehouseId = useWarehouseId();
  const activity = useTelemetryStore((s) => s.inventoryActivity);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 5_000);
    return () => clearInterval(id);
  }, []);
  const live = activityMatches(activity, siteId, warehouseId, now);
  // The denominator: registered addresses of the warehouse's maps. Asked for only while
  // a sweep is streaming — an idle warehouse has nothing to divide.
  const maps = useQuery({
    queryKey: ["inventory-maps", siteId],
    queryFn: () => listInventoryMaps(siteId!),
    enabled: live && !!siteId,
    staleTime: 5 * 60_000,
  });
  const total = useMemo(() => {
    if (!maps.data) return null;
    const mine = maps.data.filter((m) => !warehouseId || m.warehouseId === warehouseId);
    const sum = mine.reduce((acc, m) => acc + (m.locationCount ?? 0), 0);
    return sum > 0 ? sum : null;
  }, [maps.data, warehouseId]);
  return useMemo(
    () => (live ? liveCountStats(activity, total, now) : null),
    [live, activity, total, now],
  );
}
